import { inject, Service } from '@angular/core';
import { pick } from 'lodash';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Api, ApiClient } from '@app/api';
import { NotificationService } from '@core/providers/notification/notification.service';
import { VenueFromBackend } from '@shared/form-models-interfaces';
import {
  ModifiedStatementFormForBackend,
  NewStatementFormForBackend,
  Statement,
  StatementActivationChangeStatus,
  StatementModifiedStatus,
  StatementSavedStatus,
} from '@shared/interfaces/statements.interface';
import { UsersBackendService } from './users-backend.service';

/** 2400 (ValidationViolation): the statement is the last active one for some target. */
const STATEMENT_LAST_ACTIVE_ERROR_CODE = 2400;
/** 409 conflicts the delete endpoint returns when the statement cannot be removed. */
const STATEMENT_HAS_ANSWERS_ERROR_CODE = 101410;
const STATEMENT_USED_IN_SURVEY_ERROR_CODE = 101439;

/** Static failure messages by error code. The last-active case is handled separately so it can
 *  name the venues that would be left without an active question. */
const DELETE_FAILURE_MESSAGE_KEYS: Record<number, string> = {
  [STATEMENT_HAS_ANSWERS_ERROR_CODE]: 'SHARED.NOTIFICATIONS.DELETE_FAILURE_HAS_ANSWERS',
  [STATEMENT_USED_IN_SURVEY_ERROR_CODE]: 'SHARED.NOTIFICATIONS.DELETE_FAILURE_USED_IN_SURVEY',
};

const DELETE_LAST_ACTIVE_MESSAGE_KEY = 'SHARED.NOTIFICATIONS.DELETE_RESULT_FAILURE';
const DELETE_LAST_ACTIVE_VENUES_MESSAGE_KEY = 'SHARED.NOTIFICATIONS.DELETE_RESULT_FAILURE_VENUES';
const DELETE_GENERIC_MESSAGE_KEY = 'SHARED.NOTIFICATIONS.DELETE_GENERIC_FAILURE';
const DEACTIVATE_LAST_ACTIVE_MESSAGE_KEY = 'SHARED.NOTIFICATIONS.DEACTIVATE_RESULT_FAILURE';
const DEACTIVATE_LAST_ACTIVE_VENUES_MESSAGE_KEY =
  'SHARED.NOTIFICATIONS.DEACTIVATE_RESULT_FAILURE_VENUES';

interface StatementActionError {
  error?: {
    errorCode?: number;
    validationErrors?: { targets?: unknown };
  };
}

/** The backend sends the blocked venues either as `{ venues: [...] }` (account context) or as a
 *  bare `[uuid]` array (venue context). */
function extractVenueUuids(targets: unknown): string[] {
  if (Array.isArray(targets)) {
    return targets.filter((uuid): uuid is string => typeof uuid === 'string');
  }
  if (targets && typeof targets === 'object' && 'venues' in targets) {
    const venues = (targets as { venues?: unknown }).venues;
    if (Array.isArray(venues)) {
      return venues.filter((uuid): uuid is string => typeof uuid === 'string');
    }
  }
  return [];
}

/** How many venue names the dialog lists before collapsing the rest into an "…and N more" line. */
const VENUE_NAME_DISPLAY_LIMIT = 8;

function capVenueNames(names: string[]): { displayed: string[]; moreCount: number } {
  if (names.length > VENUE_NAME_DISPLAY_LIMIT + 1) {
    return {
      displayed: names.slice(0, VENUE_NAME_DISPLAY_LIMIT),
      moreCount: names.length - VENUE_NAME_DISPLAY_LIMIT,
    };
  }
  return { displayed: names, moreCount: 0 };
}

@Service()
export class StatementsBackendService {
  private api = inject(ApiClient);
  private notificationService = inject(NotificationService);
  private usersBackendService = inject(UsersBackendService);

  getActiveStatementsForAccount$(
    accountUuid: string
  ): Observable<Api.surveys.accounts.statements.GetResult> {
    return this.api.get(Api.surveys.accounts.statements, { pathParams: { accountUuid } });
  }

  getActiveStatementsForVenue$(
    venueUuid: string
  ): Observable<Api.surveys.venues.statements.GetResult> {
    return this.api.get(Api.surveys.venues.statements, { pathParams: { venueUuid } });
  }

  addNewStatement$(
    ownerUuid: string,
    statementObj: NewStatementFormForBackend
  ): Observable<StatementSavedStatus> {
    return this.api.post(Api.surveys.accounts.statements, {
      pathParams: { accountUuid: ownerUuid },
      body: pick(statementObj, [
        'categoryUuid',
        'translations',
      ]) as Api.surveys.accounts.statements.PostBody,
    });
  }

  editStatement$(
    questionUuid: string,
    statementObj: ModifiedStatementFormForBackend
  ): Observable<StatementModifiedStatus> {
    return this.api
      .put(Api.surveys.statements, {
        pathParams: { uuid: questionUuid },
        body: {
          ...pick(statementObj, ['translations']),
          accountUuid: statementObj.accountUuid,
        },
      })
      .pipe(
        map(result => ({
          ...result,
          questionCopy: statementObj,
        }))
      );
  }

  deleteStatement$(statementUuid: string, accountUuid: string): Observable<boolean> {
    return this.api
      .delete(Api.surveys.statements.accounts, {
        pathParams: { uuid: statementUuid, accountUuid },
      })
      .pipe(
        map(() => true),
        catchError((error: StatementActionError) => this.reportDeleteFailure$(error, accountUuid))
      );
  }

  private reportDeleteFailure$(
    error: StatementActionError,
    accountUuid: string
  ): Observable<boolean> {
    const errorCode = error?.error?.errorCode;

    if (errorCode === STATEMENT_LAST_ACTIVE_ERROR_CODE) {
      return this.reportLastActive$(
        error,
        accountUuid,
        DELETE_LAST_ACTIVE_VENUES_MESSAGE_KEY,
        DELETE_LAST_ACTIVE_MESSAGE_KEY
      ).pipe(map(() => false));
    }

    const messageKey =
      (errorCode !== undefined ? DELETE_FAILURE_MESSAGE_KEYS[errorCode] : undefined) ??
      DELETE_GENERIC_MESSAGE_KEY;
    this.notificationService.errorDialogWithKey(messageKey);

    return of(false);
  }

  /** Shows a "last active question" dialog, naming the venues that would be left with no active
   *  question when they can be resolved, and falling back to the category-level message otherwise. */
  private reportLastActive$(
    error: StatementActionError,
    accountUuid: string | undefined,
    venuesMessageKey: string,
    categoryMessageKey: string
  ): Observable<void> {
    const venueUuids = extractVenueUuids(error?.error?.validationErrors?.targets);

    if (venueUuids.length === 0 || !accountUuid) {
      this.notificationService.errorDialogWithKey(categoryMessageKey);
      return of(undefined);
    }

    return this.usersBackendService.getVenuesList$(accountUuid).pipe(
      map((venues: VenueFromBackend[]) => this.resolveVenueNames(venueUuids, venues)),
      catchError(() => of([] as string[])),
      map((venueNames: string[]) => {
        if (venueNames.length === 0) {
          this.notificationService.errorDialogWithKey(categoryMessageKey);
        } else {
          const { displayed, moreCount } = capVenueNames(venueNames);
          this.notificationService.errorDialogWithList(venuesMessageKey, displayed, moreCount);
        }
      })
    );
  }

  private resolveVenueNames(venueUuids: string[], venues: VenueFromBackend[]): string[] {
    const brandNameByUuid = new Map(venues.map(venue => [venue.uuid, venue.brandName]));
    return venueUuids
      .map(uuid => brandNameByUuid.get(uuid))
      .filter((brandName): brandName is string => !!brandName);
  }

  changeStatementStatusForAccount$(
    accountUuid: string,
    statement: Statement
  ): Observable<StatementActivationChangeStatus | number> {
    return this.api
      .patch(Api.surveys.accounts.statements.assignments, {
        pathParams: { accountUuid, questionUuid: statement.uuid },
        body: {
          isEnabled: statement.isEnabled ?? false,
          isEnforced: statement.isEnforced ?? false,
        },
      })
      .pipe(
        catchError((error: StatementActionError) =>
          this.reportAssignmentFailure$(error, accountUuid)
        )
      );
  }

  /** Activate/enforce a single account statement — used right after creating one. */
  setAccountStatementAssignment$(
    accountUuid: string,
    questionUuid: string,
    assignment: { isEnabled: boolean; isEnforced: boolean }
  ): Observable<StatementActivationChangeStatus> {
    return this.api.patch(Api.surveys.accounts.statements.assignments, {
      pathParams: { accountUuid, questionUuid },
      body: assignment,
    });
  }

  changeStatementStatusForVenue$(
    venueUuid: string,
    statement: Statement
  ): Observable<StatementActivationChangeStatus | number> {
    return this.api
      .patch(Api.surveys.venues.statements.assignments, {
        pathParams: { venueUuid, questionUuid: statement.uuid },
        body: {
          isEnabled: statement.isEnabled ?? false,
          isEnforced: statement.isEnforced ?? false,
        },
      })
      .pipe(catchError((error: StatementActionError) => this.reportAssignmentFailure$(error)));
  }

  /**
   * The last-active conflict (2400) is reported in a dialog, naming the affected venues when an
   * account context is available. Every failure emits its error code so the caller can tell it apart
   * from a success payload and revert the toggle.
   */
  private reportAssignmentFailure$(
    error: StatementActionError,
    accountUuid?: string
  ): Observable<number> {
    const errorCode = error?.error?.errorCode;

    if (errorCode === STATEMENT_LAST_ACTIVE_ERROR_CODE) {
      return this.reportLastActive$(
        error,
        accountUuid,
        DEACTIVATE_LAST_ACTIVE_VENUES_MESSAGE_KEY,
        DEACTIVATE_LAST_ACTIVE_MESSAGE_KEY
      ).pipe(map(() => STATEMENT_LAST_ACTIVE_ERROR_CODE));
    }

    return of(errorCode ?? 0);
  }
}
